import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  LinearProgress,
  Badge,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { KYCService, KYC_STATUS } from '../../../utils/kycService';
import { handleKYCError, handleKYCSuccess } from '../../../utils/kycUtils';

// Statistics Card Component
const StatCard = ({ title, value, icon, color, trend, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="text.secondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <TrendingUpIcon fontSize="small" color={trend > 0 ? 'success' : 'error'} />
              <Typography 
                variant="body2" 
                color={trend > 0 ? 'success.main' : 'error.main'}
              >
                {Math.abs(trend)}% vs last month
              </Typography>
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: '50%',
            backgroundColor: `${color}.light`,
            color: `${color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// KYC Record Row Component
const KYCRecordRow = ({ record, onView, onApprove, onReject, onExport }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: '', record: null });

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    setActionDialog({ open: true, action, record });
    handleMenuClose();
  };

  const handleActionConfirm = () => {
    const { action, record } = actionDialog;
    switch (action) {
      case 'view':
        onView(record);
        break;
      case 'approve':
        onApprove(record);
        break;
      case 'reject':
        onReject(record);
        break;
      case 'export':
        onExport(record);
        break;
    }
    setActionDialog({ open: false, action: '', record: null });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case KYC_STATUS.VERIFIED:
        return 'success';
      case KYC_STATUS.PENDING:
        return 'warning';
      case KYC_STATUS.PROCESSING:
        return 'info';
      case KYC_STATUS.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body2" fontWeight="medium">
              {record.user_name}
            </Typography>
            <Badge
              badgeContent={record.verification_count || 0}
              color="primary"
              max={99}
            >
              <PeopleIcon color="action" />
            </Badge>
          </Stack>
        </TableCell>
        <TableCell>{record.email}</TableCell>
        <TableCell>{record.phone}</TableCell>
        <TableCell>
          <Chip
            label={record.pan_status?.toUpperCase() || 'NOT STARTED'}
            color={getStatusColor(record.pan_status)}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={record.aadhaar_status?.toUpperCase() || 'NOT STARTED'}
            color={getStatusColor(record.aadhaar_status)}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={record.bank_status?.toUpperCase() || 'NOT STARTED'}
            color={getStatusColor(record.bank_status)}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {new Date(record.created_at).toLocaleDateString()}
          </Typography>
        </TableCell>
        <TableCell>
          <IconButton onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
        </TableCell>
      </TableRow>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleAction('view')}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem 
          onClick={() => handleAction('approve')}
          disabled={record.pan_status === KYC_STATUS.VERIFIED && 
                   record.aadhaar_status === KYC_STATUS.VERIFIED && 
                   record.bank_status === KYC_STATUS.VERIFIED}
        >
          <ApproveIcon sx={{ mr: 1 }} />
          Approve
        </MenuItem>
        <MenuItem onClick={() => handleAction('reject')}>
          <RejectIcon sx={{ mr: 1 }} />
          Reject
        </MenuItem>
        <MenuItem onClick={() => handleAction('export')}>
          <ExportIcon sx={{ mr: 1 }} />
          Export
        </MenuItem>
      </Menu>

      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, action: '', record: null })}
      >
        <DialogTitle>
          {actionDialog.action === 'approve' && 'Approve KYC Verification'}
          {actionDialog.action === 'reject' && 'Reject KYC Verification'}
          {actionDialog.action === 'view' && 'KYC Details'}
          {actionDialog.action === 'export' && 'Export KYC Data'}
        </DialogTitle>
        <DialogContent>
          {actionDialog.action === 'approve' && (
            <Alert severity="success">
              Are you sure you want to approve this KYC verification?
            </Alert>
          )}
          {actionDialog.action === 'reject' && (
            <Alert severity="warning">
              Are you sure you want to reject this KYC verification? This action cannot be undone.
            </Alert>
          )}
          {actionDialog.action === 'view' && actionDialog.record && (
            <Box>
              <Typography variant="h6" gutterBottom>User Information</Typography>
              <Typography><strong>Name:</strong> {actionDialog.record.user_name}</Typography>
              <Typography><strong>Email:</strong> {actionDialog.record.email}</Typography>
              <Typography><strong>Phone:</strong> {actionDialog.record.phone}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Verification Status</Typography>
              <Stack direction="row" spacing={2}>
                <Chip
                  label={`PAN: ${actionDialog.record.pan_status?.toUpperCase() || 'NOT STARTED'}`}
                  color={getStatusColor(actionDialog.record.pan_status)}
                />
                <Chip
                  label={`Aadhaar: ${actionDialog.record.aadhaar_status?.toUpperCase() || 'NOT STARTED'}`}
                  color={getStatusColor(actionDialog.record.aadhaar_status)}
                />
                <Chip
                  label={`Bank: ${actionDialog.record.bank_status?.toUpperCase() || 'NOT STARTED'}`}
                  color={getStatusColor(actionDialog.record.bank_status)}
                />
              </Stack>
            </Box>
          )}
          {actionDialog.action === 'export' && (
            <Alert severity="info">
              This will download the KYC data for {actionDialog.record?.user_name} in PDF format.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, action: '', record: null })}>
            Cancel
          </Button>
          <Button 
            onClick={handleActionConfirm}
            variant="contained"
            color={actionDialog.action === 'reject' ? 'error' : 'primary'}
          >
            {actionDialog.action === 'approve' && 'Approve'}
            {actionDialog.action === 'reject' && 'Reject'}
            {actionDialog.action === 'view' && 'Close'}
            {actionDialog.action === 'export' && 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Main Admin Dashboard Component
const KYCAdminDashboard = () => {
  const [kycRecords, setKycRecords] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  useEffect(() => {
    loadKYCData();
    loadStatistics();
  }, [currentPage, searchTerm, statusFilter]);

  const loadKYCData = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        status: statusFilter
      };
      
      const response = await KYCService.getAllKYCRecords(params);
      
      if (response?.data?.status) {
        setKycRecords(response.data.data.records);
        setTotalPages(Math.ceil(response.data.data.total / 10));
      }
    } catch (error) {
      handleKYCError(error, 'Loading KYC records');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await KYCService.getKYCStatistics();
      
      if (response?.data?.status) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      handleKYCError(error, 'Loading statistics');
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleBulkAction = async (action) => {
    if (selectedRecords.length === 0) {
      toast.error('Please select records to perform bulk action');
      return;
    }

    try {
      const response = await KYCService.bulkVerify(selectedRecords, action);
      
      if (response?.data?.status) {
        handleKYCSuccess(`${action} action completed successfully`);
        setSelectedRecords([]);
        loadKYCData();
      }
    } catch (error) {
      handleKYCError(error, 'Bulk action');
    }
  };

  const handleExportAll = async () => {
    try {
      const response = await KYCService.exportKYCData({
        search: searchTerm,
        status: statusFilter
      }, 'csv');
      
      if (response?.data) {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `kyc-records-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        handleKYCSuccess('Data exported successfully');
      }
    } catch (error) {
      handleKYCError(error, 'Export data');
    }
  };

  const handleRecordAction = async (action, record) => {
    try {
      switch (action) {
        case 'approve':
          await KYCService.updateKYCStatus(record.id, { status: KYC_STATUS.VERIFIED });
          handleKYCSuccess('KYC approved successfully');
          break;
        case 'reject':
          await KYCService.updateKYCStatus(record.id, { status: KYC_STATUS.REJECTED });
          handleKYCSuccess('KYC rejected successfully');
          break;
        case 'export':
          await KYCService.exportKYCData({ userId: record.user_id }, 'pdf');
          handleKYCSuccess('KYC data exported successfully');
          break;
      }
      loadKYCData();
    } catch (error) {
      handleKYCError(error, `${action} action`);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">
          KYC Admin Dashboard
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadKYCData}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportAll}
          >
            Export All
          </Button>
        </Stack>
      </Stack>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Verifications"
            value={statistics.total_verifications || 0}
            icon={<PeopleIcon />}
            color="primary"
            trend={statistics.verification_trend}
            subtitle="All time"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Review"
            value={statistics.pending_count || 0}
            icon={<ScheduleIcon />}
            color="warning"
            trend={statistics.pending_trend}
            subtitle="Awaiting approval"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verified Today"
            value={statistics.verified_today || 0}
            icon={<ApproveIcon />}
            color="success"
            trend={statistics.today_trend}
            subtitle="Completed today"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Success Rate"
            value={`${statistics.success_rate || 0}%`}
            icon={<TrendingUpIcon />}
            color="info"
            trend={statistics.success_trend}
            subtitle="Last 30 days"
          />
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilter}
                label="Status Filter"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value={KYC_STATUS.PENDING}>Pending</MenuItem>
                <MenuItem value={KYC_STATUS.PROCESSING}>Processing</MenuItem>
                <MenuItem value={KYC_STATUS.VERIFIED}>Verified</MenuItem>
                <MenuItem value={KYC_STATUS.REJECTED}>Rejected</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Bulk Actions */}
          {selectedRecords.length > 0 && (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedRecords.length} record(s) selected
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={() => handleBulkAction('approve')}
              >
                Approve Selected
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleBulkAction('reject')}
              >
                Reject Selected
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* KYC Records Table */}
      <Card>
        <CardContent>
          {isLoading && <LinearProgress />}
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>PAN Status</TableCell>
                  <TableCell>Aadhaar Status</TableCell>
                  <TableCell>Bank Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kycRecords.map((record) => (
                  <KYCRecordRow
                    key={record.id}
                    record={record}
                    onView={(record) => handleRecordAction('view', record)}
                    onApprove={(record) => handleRecordAction('approve', record)}
                    onReject={(record) => handleRecordAction('reject', record)}
                    onExport={(record) => handleRecordAction('export', record)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default KYCAdminDashboard;
